import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Users, 
  Clock, 
  RefreshCw,
  Building2,
  MapPin,
  Search,
  Download,
  CalendarIcon,
  Filter,
  ChevronDown,
  ArrowUpDown,
  LogOut,
  LogIn,
  RotateCcw,
  Briefcase
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============= STATUS BADGE COMPONENT =============
const StatusBadge = ({ status }) => {
  const statusConfig = {
    out: { label: 'Out', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    completed: { label: 'Completed', className: 'bg-green-100 text-green-800 border-green-300' },
    midway_returned: { label: 'Midway Return', className: 'bg-orange-100 text-orange-800 border-orange-300' },
    auto_closed: { label: 'Auto Closed', className: 'bg-gray-100 text-gray-800 border-gray-300' }
  };
  
  const config = statusConfig[status] || { label: status, className: 'bg-gray-100' };
  
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

// ============= FORMAT HELPERS =============
const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleString('en-IN', { 
      day: '2-digit',
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  } catch {
    return '-';
  }
};

const formatTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  } catch {
    return '-';
  }
};

const formatDuration = (mins) => {
  if (!mins && mins !== 0) return '-';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return `${hours}h ${minutes}m`;
};

const formatDate = (date) => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
};

// ============= MAIN COMPONENT =============
const EmployeeMovementReportPage = () => {
  // State
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    fromDate: new Date(new Date().setDate(new Date().getDate() - 7)), // Last 7 days default
    toDate: new Date(),
    unit: '',
    employeeId: '',
    status: '',
    destinationType: ''
  });
  
  const [showFilters, setShowFilters] = useState(true);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    out: 0,
    midwayReturned: 0,
    autoClosed: 0,
    interUnit: 0,
    outsideWork: 0,
    avgDuration: 0
  });

  // Get auth token
  const getToken = () => localStorage.getItem('token');

  // ============= DATA FETCHING =============
  
  const fetchUnits = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setUnits(response.data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hr/employees`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.fromDate) params.append('from_date', formatDate(filters.fromDate));
      if (filters.toDate) params.append('to_date', formatDate(filters.toDate));
      if (filters.unit) params.append('unit', filters.unit);
      if (filters.employeeId) params.append('employee_id', filters.employeeId);
      if (filters.status) params.append('status', filters.status);
      if (filters.destinationType) params.append('destination_type', filters.destinationType);
      params.append('limit', '500');
      
      const response = await axios.get(
        `${API_URL}/api/employee-movements/report?${params}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      const data = response.data?.movements || response.data || [];
      setMovements(data);
      
      // Calculate stats
      const completed = data.filter(m => m.status === 'completed').length;
      const out = data.filter(m => m.status === 'out').length;
      const midwayReturned = data.filter(m => m.status === 'midway_returned').length;
      const autoClosed = data.filter(m => m.status === 'auto_closed').length;
      const interUnit = data.filter(m => m.destination_type === 'inter_unit').length;
      const outsideWork = data.filter(m => m.destination_type === 'outside_work').length;
      const durationsWithValue = data.filter(m => m.duration_mins).map(m => m.duration_mins);
      const avgDuration = durationsWithValue.length > 0 
        ? Math.round(durationsWithValue.reduce((a, b) => a + b, 0) / durationsWithValue.length)
        : 0;
      
      setStats({
        total: data.length,
        completed,
        out,
        midwayReturned,
        autoClosed,
        interUnit,
        outsideWork,
        avgDuration
      });
    } catch (error) {
      console.error('Error fetching movements:', error);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUnits();
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  // ============= EXPORT TO CSV =============
  const exportToCSV = () => {
    if (movements.length === 0) return;
    
    const headers = [
      'Date', 'Movement No', 'Employee Name', 'Employee Code', 'Designation',
      'Source Unit', 'Destination', 'Type', 'Out Time', 'Out By',
      'In Time', 'In By', 'Duration (mins)', 'Status', 'Purpose'
    ];
    
    const rows = movements.map(m => [
      m.date,
      m.movement_no,
      m.employee_name,
      m.employee_code,
      m.designation || '',
      m.source_unit,
      m.destination_type === 'outside_work' ? m.outside_details : m.destination_unit,
      m.destination_type === 'outside_work' ? 'Outside Work' : 'Inter-Unit',
      m.out_at ? new Date(m.out_at).toLocaleString('en-IN') : '',
      m.out_by_name || '',
      m.in_at ? new Date(m.in_at).toLocaleString('en-IN') : '',
      m.in_by_name || '',
      m.duration_mins || '',
      m.status,
      m.purpose || ''
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `employee_movements_${formatDate(filters.fromDate)}_to_${formatDate(filters.toDate)}.csv`;
    link.click();
  };

  // ============= RENDER =============
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            Employee Movement Report
          </h1>
          <p className="text-sm text-gray-500 mt-1">Track and analyze employee movements across units</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button variant="outline" onClick={fetchMovements}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={exportToCSV} disabled={movements.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* From Date */}
              <div className="space-y-2">
                <Label>From Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.fromDate ? format(filters.fromDate, 'dd MMM yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={filters.fromDate}
                      onSelect={(date) => setFilters(prev => ({ ...prev, fromDate: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* To Date */}
              <div className="space-y-2">
                <Label>To Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.toDate ? format(filters.toDate, 'dd MMM yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={filters.toDate}
                      onSelect={(date) => setFilters(prev => ({ ...prev, toDate: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Unit Filter */}
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select 
                  value={filters.unit || "all"} 
                  onValueChange={(val) => setFilters(prev => ({ ...prev, unit: val === "all" ? "" : val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Units" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Units</SelectItem>
                    {units.map(unit => (
                      <SelectItem key={unit.id} value={unit.code}>
                        {unit.code} - {unit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Employee Filter */}
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select 
                  value={filters.employeeId || "all"} 
                  onValueChange={(val) => setFilters(prev => ({ ...prev, employeeId: val === "all" ? "" : val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={filters.status || "all"} 
                  onValueChange={(val) => setFilters(prev => ({ ...prev, status: val === "all" ? "" : val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="out">Out</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="midway_returned">Midway Returned</SelectItem>
                    <SelectItem value="auto_closed">Auto Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Movement Type Filter */}
              <div className="space-y-2">
                <Label>Movement Type</Label>
                <Select 
                  value={filters.destinationType || "all"} 
                  onValueChange={(val) => setFilters(prev => ({ ...prev, destinationType: val === "all" ? "" : val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="inter_unit">Inter-Unit</SelectItem>
                    <SelectItem value="outside_work">Outside Work</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Clear Filters */}
            <div className="mt-4 flex justify-end">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setFilters({
                  fromDate: new Date(new Date().setDate(new Date().getDate() - 7)),
                  toDate: new Date(),
                  unit: '',
                  employeeId: '',
                  status: '',
                  destinationType: ''
                })}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            <p className="text-xs text-gray-500">Completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.out}</p>
            <p className="text-xs text-gray-500">Currently Out</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{stats.midwayReturned}</p>
            <p className="text-xs text-gray-500">Midway Return</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-600">{stats.autoClosed}</p>
            <p className="text-xs text-gray-500">Auto Closed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.interUnit}</p>
            <p className="text-xs text-gray-500">Inter-Unit</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.outsideWork}</p>
            <p className="text-xs text-gray-500">Outside Work</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-indigo-600">{formatDuration(stats.avgDuration)}</p>
            <p className="text-xs text-gray-500">Avg Duration</p>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Movement Records ({movements.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : movements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No movements found for the selected filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Mov. No.</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Out Time</TableHead>
                    <TableHead>Out By</TableHead>
                    <TableHead>In Time</TableHead>
                    <TableHead>In By</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="text-sm whitespace-nowrap">{m.date}</TableCell>
                      <TableCell className="font-mono text-xs">{m.movement_no}</TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{m.employee_name}</div>
                        <div className="text-xs text-gray-500">{m.employee_code}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{m.source_unit}</Badge>
                      </TableCell>
                      <TableCell>
                        {m.destination_type === 'outside_work' ? (
                          <div className="text-sm max-w-[150px] truncate" title={m.outside_details}>
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {m.outside_details}
                          </div>
                        ) : (
                          <Badge variant="outline">{m.destination_unit || '-'}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {m.destination_type === 'outside_work' ? (
                          <Badge className="bg-purple-100 text-purple-800 border-purple-300">
                            <Briefcase className="w-3 h-3 mr-1" />
                            Outside
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                            <Building2 className="w-3 h-3 mr-1" />
                            Inter-Unit
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{formatTime(m.out_at)}</TableCell>
                      <TableCell className="text-sm">{m.out_by_name || '-'}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{formatTime(m.in_at)}</TableCell>
                      <TableCell className="text-sm">{m.in_by_name || '-'}</TableCell>
                      <TableCell className="text-sm font-medium">{formatDuration(m.duration_mins)}</TableCell>
                      <TableCell><StatusBadge status={m.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeMovementReportPage;
